import React, { useEffect, useState } from "react";

import { Formik, Form, FieldArray } from "formik";
import { Plus } from "icons";

import businessTermsApi from "apis/businessTerms";
import usersApi from "apis/users";
import { Button, PageLoader, Pane, Typography } from "clearsense-ui/components";
import { Input, Select, Textarea } from "clearsense-ui/components/formik";

import AddVariation from "./AddVariation";
import VariationAccordion from "./VariationAccordion";

import {
  CREATE_BUSINESS_GLOSSARY_INITIAL_VALUES,
  CREATE_BUSINESS_GLOSSARY_VALIDATION_SCHEMA,
} from "../constants/businessGlossaryTable";

const NewBusinessTerms = ({
  isNewPaneOpen,
  setIsNewPaneOpen,
  isEdit,
  setIsEdit,
  selectedBusinessTerm,
  fetchBusinessTerms,
}) => {
  const [isStewardsLoading, setIsStewardsLoading] = useState(false);
  const [stewards, setStewards] = useState([]);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState();
  const [isAddVariation, setIsAddVariation] = useState(false);
  const [isEditVariation, setIsEditVariation] = useState(false);

  const fetchStewards = async () => {
    try {
      setIsStewardsLoading(true);
      const { data } = await usersApi.fetch(1, 1000, "");
      setStewards(
        data?.users?.map(user => ({ label: user.name, value: user.key }))
      );
    } catch (error) {
      logger.log(error);
    } finally {
      setIsStewardsLoading(false);
    }
  };

  const handleSubmit = async values => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        approver_id: values.approver.value,
        drafter_id: values.drafter.value,
        variations_attributes: values.variations,
      };
      isEdit
        ? await businessTermsApi.update(selectedBusinessTerm.id, payload)
        : await businessTermsApi.create(payload);
      fetchBusinessTerms();
    } catch (error) {
      logger.error(error);
    } finally {
      setIsNewPaneOpen(false);
      setIsEdit(false);
    }
  };

  const addNewVariation = addVariation => {
    setIsEditVariation(false);
    setIsAddVariation(true);
    setSelectedVariationIndex();
    addVariation({ name: "", description: "" });
  };

  const handleUpdateVariation = index => {
    setSelectedVariationIndex(index);
    setIsEditVariation(true);
    setIsAddVariation(false);
  };

  const onVariationClose = () => {
    setIsAddVariation(false);
    setIsEditVariation(false);
    setSelectedVariationIndex();
  };

  const onPaneClose = () => {
    setIsNewPaneOpen(false);
    setIsEdit(false);
  };

  useEffect(() => {
    fetchStewards();
  }, []);

  const renderVariationFields = (
    variationIndex,
    removeVariation,
    variation,
    variationsCount
  ) => {
    if (isAddVariation && variationIndex === variationsCount - 1) {
      return (
        <AddVariation
          key={variationIndex}
          variationIndex={variationIndex}
          isEditVariation={isEditVariation}
          onVariationClose={onVariationClose}
          removeVariation={removeVariation}
        />
      );
    }

    return selectedVariationIndex === variationIndex ? (
      <AddVariation
        key={variationIndex}
        variationIndex={variationIndex}
        isEditVariation={isEditVariation}
        onVariationClose={onVariationClose}
        removeVariation={removeVariation}
      />
    ) : (
      <VariationAccordion
        key={variationIndex}
        variationIndex={variationIndex}
        variation={variation}
        handleUpdateVariation={handleUpdateVariation}
      />
    );
  };

  return (
    <Pane isOpen={isNewPaneOpen} onClose={onPaneClose}>
      <Pane.Header>
        <Typography
          style="h3"
          type="bold"
          className="-mt-3"
          data-cy="business-term-pane-title"
        >
          {isEdit ? "Edit" : "Create"} Business Term
        </Typography>
      </Pane.Header>
      {isStewardsLoading ? (
        <PageLoader />
      ) : (
        <Formik
          initialValues={
            isEdit
              ? {
                  ...selectedBusinessTerm,
                  drafter: {
                    value: selectedBusinessTerm.drafter?.id,
                    label: selectedBusinessTerm.drafter?.name,
                  },
                  approver: {
                    value: selectedBusinessTerm.approver?.id,
                    label: selectedBusinessTerm.approver?.name,
                  },
                }
              : CREATE_BUSINESS_GLOSSARY_INITIAL_VALUES
          }
          validationSchema={CREATE_BUSINESS_GLOSSARY_VALIDATION_SCHEMA}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values }) => (
            <Form>
              <Pane.Body>
                <div className="flex w-full flex-col overflow-auto space-y-4">
                  <Input
                    data-cy="business-term-name-input-field"
                    label="Name"
                    name="name"
                    placeholder="Enter Business Term name"
                    required
                  />
                  <Textarea
                    data-cy="business-term-description-textarea"
                    label="Description"
                    name="description"
                    placeholder="Enter description"
                  />
                  <Select
                    data-cy="drafter-select-input-field"
                    label="Drafter"
                    placeholder="Select Steward"
                    name="drafter"
                    options={stewards}
                    strategy="fixed"
                    required
                  />
                  <Select
                    data-cy="approver-select-input-field"
                    label="Approver"
                    placeholder="Select Steward"
                    name="approver"
                    strategy="fixed"
                    options={stewards}
                    required
                  />
                  <Typography>
                    Variations to the definition of this business term
                  </Typography>
                  <FieldArray name="variations">
                    {({ remove, push }) => (
                      <div className="flex flex-col space-y-2">
                        {values.variations?.length > 0 &&
                          values.variations?.map((variation, index) =>
                            renderVariationFields(
                              index,
                              remove,
                              variation,
                              values.variations?.length
                            )
                          )}
                        <div className="flex">
                          <Button
                            data-cy="add-variation-button"
                            onClick={() => addNewVariation(push)}
                            disabled={isAddVariation}
                            label="Create Variation"
                            icon={Plus}
                            iconPosition="left"
                          />
                        </div>
                      </div>
                    )}
                  </FieldArray>
                </div>
              </Pane.Body>
              <Pane.Footer className="gap-1">
                <Button
                  data-cy="save-business-term-button"
                  label="Save and Close"
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
                <Button
                  data-cy="cancel-business-term-button"
                  label="Cancel"
                  style="secondary"
                  onClick={onPaneClose}
                />
              </Pane.Footer>
            </Form>
          )}
        </Formik>
      )}
    </Pane>
  );
};

export default NewBusinessTerms;
